import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import { useEffect, useMemo, useState } from "react";
import { RowBlockProps, RowLayout } from "./types";
import { RowLayoutPresets } from "./index";
import {
  FloatingPanelContent,
  FloatingPanelSeparator,
} from "@babackapp/ui/components/floating-panel";
import { Block, BlockType } from "../blocks-types";
import PanelInlineContent from "../../lib/panel/panel-inline-content";
import { getEditorBlockConfig } from "../index";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { Button } from "@babackapp/ui/components/button";
import { CircleMinus } from "lucide-react";
import useLibraryCompatibleBlocks from "./use-library-compatible-blocks";
import { BlockPanelProps } from "../../lib/register-block";
import { useTranslation } from "react-i18next";

export function EmailPanelSubBlocks() {
  const { block } = useBlockPanelPropsContext() as RowBlockProps;
  const [selectedTab, setSelectedTab] = useState(0);
  const layoutId: RowLayout = block.properties?.layout || "6-6";
  const subBlock = block.content?.children?.[selectedTab] || null;

  useEffect(() => {
    if (layoutId) {
      const layoutCellsCount = layoutId.split("-").length;
      // If the current selected tab is out of range, reset to the last available tab
      if (selectedTab >= layoutCellsCount) {
        setSelectedTab(layoutCellsCount - 1);
      }
    }
  }, [layoutId]);

  return (
    <>
      <SubBlocksTabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
      <FloatingPanelSeparator />
      <div className="bg-muted/50">
        <BlockSelector subBlock={subBlock} blockIndex={selectedTab} />
        {!!subBlock && (
          <>
            <FloatingPanelSeparator />
            <SubBlockPanel block={subBlock} blockIndex={selectedTab} />
          </>
        )}
      </div>
    </>
  );
}

function SubBlockPanel({
  block,
  blockIndex,
}: {
  block: Block;
  blockIndex: number;
}) {
  const { t } = useTranslation("editor-blocks-library");
  const blockProps =
    useBlockPanelPropsContext() as BlockPanelProps<RowBlockProps>;
  const blockType = block?.type;
  const blockConfig = getEditorBlockConfig(blockType);
  const formAPI = useSubBlockFormApi(blockIndex);

  if (!blockType || !blockConfig?.EmailConfigPanel) {
    return (
      <div className="p-2 text-sm text-muted-foreground">
        {t(
          "editorBlocksLibrary.blocks.row.panel.subBlocks.noSettings",
          "No settings available for this block.",
        )}
      </div>
    );
  }
  const EmailPanel = blockConfig.EmailConfigPanel;

  return (
    <EmailPanel
      {...blockProps}
      // section={section}
      block={block as never}
      index={blockIndex}
      formAPI={formAPI}
      isWrapped
    />
  );
}

function BlockSelector({
  subBlock,
  blockIndex,
}: {
  subBlock: Block | null;
  blockIndex: number;
}) {
  const { t } = useTranslation("editor-blocks-library");
  const { formAPI, block } = useBlockPanelPropsContext() as RowBlockProps;
  const layoutId: RowLayout = block.properties?.layout || "6-6";
  const currentCellSize = parseInt(layoutId.split("-")[blockIndex], 10);
  const blocks = useLibraryCompatibleBlocks("email", currentCellSize);

  const handleInsert = <T extends BlockType>(blockType: T) => {
    const properties: Record<string, unknown> = {};
    const subBlocks = block.content?.children || [];

    subBlocks[blockIndex] = {
      id: crypto.randomUUID(),
      type: blockType,
      alignment: "center",
      properties,
    } as Block<T>;
    formAPI.updateField(`${formAPI.blockPath}.content`, {
      ...block.content,
      children: subBlocks,
    });
  };

  const handleRemove = () => {
    const subBlocks = block.content?.children || [];
    subBlocks[blockIndex] = null;
    formAPI.updateField(`${formAPI.blockPath}.content`, {
      ...block.content,
      children: subBlocks,
    });
  };

  if (!subBlock) {
    return (
      <div className="flex flex-wrap gap-2 p-2">
        <div className="w-full text-xs text-muted-foreground">
          {t(
            "editorBlocksLibrary.blocks.row.panel.subBlocks.selectBlock",
            "Select a block",
          )}
        </div>
        {blocks.map((b) => {
          const Icon = b!.config.Icon;

          return (
            <Tooltip key={b?.config.type!}>
              <TooltipTrigger>
                <div
                  className="flex items-center justify-center p-2 border border-dashed border-border rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  onClick={() => handleInsert(b!.config.type as BlockType)}
                >
                  <Icon className="inline h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>{t(b?.config.name || "")}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  const blockConfig = getEditorBlockConfig(subBlock.type);

  return (
    <PanelInlineContent
      largeTitle
      title={t(blockConfig?.config.name || "")}
      Icon={blockConfig?.config.Icon}
    >
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        onClick={handleRemove}
      >
        <CircleMinus className="h-3.5 w-3.5" />
      </Button>
    </PanelInlineContent>
  );
}

function SubBlocksTabs({
  selectedTab,
  setSelectedTab,
}: {
  selectedTab: number;
  setSelectedTab: (index: number) => void;
}) {
  const blockProps = useBlockPanelPropsContext() as RowBlockProps;
  const layoutId: RowLayout = blockProps.block.properties?.layout || "6-6";

  const p = RowLayoutPresets[layoutId];
  const Preview = p.Preview;

  return (
    <FloatingPanelContent className="pb-0 pt-4 mb-2">
      <Preview
        displayLabels
        className="h-6 cursor-pointer"
        selectedIndex={selectedTab}
        onSelect={setSelectedTab}
      />
    </FloatingPanelContent>
  );
}

export default function useSubBlockFormApi(index: number) {
  const {
    formAPI,
    index: parentIndex,
    section,
  } = useBlockPanelPropsContext() as RowBlockProps;
  const blockPath = `content.${section}Blocks.${parentIndex}.content.children.${index}`;

  return useMemo(
    () => ({
      blockPath,
      updateField: (path: string, value: unknown) => {
        formAPI.updateField(path, value);
      },
      getFieldValue: (path: string) => formAPI.getFieldValue(path),
      registerImage: (imageUploadKey: string, file: File | null) => {
        formAPI.registerImage(imageUploadKey, file);
      },
    }),
    [formAPI, blockPath],
  );
}
